import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AirIcon from "@mui/icons-material/Air";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  addDoc,
  collection,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { format, formatDistanceToNow, isSameDay } from "date-fns";
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  addYears,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { auth, db } from "../../firebase/firebase.js";
import BreathingTimeline from "./BreathingTimeline.js";

const WEEK_OPTIONS = { weekStartsOn: 1 };

const MS_IN_HOUR = 3.6e6;
const MS_IN_DAY = 86_400_000;
const MS_IN_WEEK = MS_IN_DAY * 7;
const MS_IN_MONTH = MS_IN_DAY * 30;
const MS_IN_YEAR = MS_IN_DAY * 365;

const SCALE_OPTIONS = [
  {
    value: "hours",
    label: "Hours",
    truncate: (date) => startOfHour(date),
    advance: (date) => addHours(date, 1),
    approxDurationMs: MS_IN_HOUR,
    tickFormatter: (date) => format(date, "ha"),
    tooltipFormatter: (date) => format(date, "MMM d • ha"),
  },
  {
    value: "days",
    label: "Days",
    truncate: (date) => startOfDay(date),
    advance: (date) => addDays(date, 1),
    approxDurationMs: MS_IN_DAY,
    tickFormatter: (date) => format(date, "MMM d"),
    tooltipFormatter: (date) => format(date, "MMMM d, yyyy"),
  },
  {
    value: "weeks",
    label: "Weeks",
    truncate: (date) => startOfWeek(date, WEEK_OPTIONS),
    advance: (date) => addWeeks(date, 1),
    approxDurationMs: MS_IN_WEEK,
    tickFormatter: (date) => format(date, "'W'w"),
    tooltipFormatter: (date) =>
      `${format(date, "MMM d")} – ${format(addDays(date, 6), "MMM d, yyyy")}`,
  },
  {
    value: "months",
    label: "Months",
    truncate: (date) => startOfMonth(date),
    advance: (date) => addMonths(date, 1),
    approxDurationMs: MS_IN_MONTH,
    tickFormatter: (date) => format(date, "MMM yyyy"),
    tooltipFormatter: (date) => format(date, "MMMM yyyy"),
  },
  {
    value: "years",
    label: "Years",
    truncate: (date) => startOfYear(date),
    advance: (date) => addYears(date, 1),
    approxDurationMs: MS_IN_YEAR,
    tickFormatter: (date) => format(date, "yyyy"),
    tooltipFormatter: (date) => format(date, "yyyy"),
  },
];

const scaleMap = SCALE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option;
  return acc;
}, {});

const SAFE_COLLECTION_NAME = "breathingLogs";

const BreathingPage = () => {
  const theme = useTheme();
  const [userId, setUserId] = useState(null);
  const [isLogging, setIsLogging] = useState(false);
  const [logSuccess, setLogSuccess] = useState(false);
  const [timelineScale, setTimelineScale] = useState("days");
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const accentTone = useMemo(
    () =>
      theme.palette.secondary?.main ??
      theme.palette.primary.light ??
      theme.palette.primary.main,
    [theme]
  );

  const pageBackground = useMemo(() => {
    const base =
      theme.palette.mode === "light"
        ? theme.palette.grey[100]
        : theme.palette.background.default;
    const primaryGlow = alpha(
      theme.palette.primary.main,
      theme.palette.mode === "light" ? 0.12 : 0.3
    );
    const accentGlow = alpha(
      accentTone,
      theme.palette.mode === "light" ? 0.1 : 0.26
    );
    return `radial-gradient(circle at -10% -25%, ${primaryGlow} 0%, transparent 60%), radial-gradient(circle at 120% -15%, ${accentGlow} 0%, transparent 55%), ${base}`;
  }, [accentTone, theme]);

  const panelSurface = useMemo(
    () => ({
      position: "relative",
      overflow: "hidden",
      borderRadius: 4,
      background:
        theme.palette.mode === "light"
          ? `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.95
            )} 0%, ${alpha(accentTone, 0.08)} 120%)`
          : `linear-gradient(135deg, ${alpha(
              theme.palette.primary.dark ?? theme.palette.primary.main,
              0.52
            )} 0%, ${alpha(accentTone, 0.32)} 120%)`,
      border: `1px solid ${alpha(
        theme.palette.primary.main,
        theme.palette.mode === "light" ? 0.16 : 0.42
      )}`,
      boxShadow: theme.shadows[6],
      backdropFilter: "blur(18px)",
    }),
    [accentTone, theme]
  );

  const fetchUserId = useCallback(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      setUserId(currentUser.uid);
    }
  }, []);

  useEffect(() => {
    fetchUserId();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUserId(user ? user.uid : null);
    });
    return () => unsubscribe();
  }, [fetchUserId]);

  useEffect(() => {
    if (!userId) {
      setEntries([]);
      setLoadingEntries(false);
      return;
    }

    setLoadingEntries(true);
    const logsRef = collection(db, SAFE_COLLECTION_NAME);
    const logsQuery = query(logsRef, where("uid", "==", userId));

    const unsubscribe = onSnapshot(
      logsQuery,
      (snapshot) => {
        const nextEntries = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const createdAt =
            data?.createdAt && typeof data.createdAt.toDate === "function"
              ? data.createdAt.toDate()
              : null;
          const clientTimestamp = data?.clientTimestamp
            ? new Date(data.clientTimestamp)
            : null;
          const timestamp = createdAt ?? clientTimestamp;
          if (timestamp && !Number.isNaN(timestamp.getTime())) {
            nextEntries.push({
              id: docSnap.id,
              timestamp,
            });
          }
        });

        nextEntries.sort(
          (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
        );
        setEntries(nextEntries);
        setErrorMessage("");
        setLoadingEntries(false);
      },
      (error) => {
        console.error("Error fetching breathing logs:", error);
        setEntries([]);
        setErrorMessage(
          "We couldn't load your breathing log right now. Please try again shortly."
        );
        setLoadingEntries(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  useEffect(() => {
    if (!logSuccess) {
      return undefined;
    }
    const timeout = setTimeout(() => setLogSuccess(false), 2200);
    return () => clearTimeout(timeout);
  }, [logSuccess]);

  const handleLogBreath = async () => {
    if (!userId) {
      setErrorMessage("Please sign in to log your deep breaths.");
      return;
    }

    setIsLogging(true);
    setErrorMessage("");
    try {
      await addDoc(collection(db, SAFE_COLLECTION_NAME), {
        uid: userId,
        createdAt: serverTimestamp(),
        clientTimestamp: new Date().toISOString(),
      });
      setLogSuccess(true);
    } catch (error) {
      console.error("Failed to log deep breath:", error);
      setErrorMessage(
        "Something went wrong while logging your deep breath. Please try again."
      );
    } finally {
      setIsLogging(false);
    }
  };

  const aggregatedSeries = useMemo(() => {
    const config = scaleMap[timelineScale];
    if (!config) {
      return [];
    }
    const grouped = new Map();
    entries.forEach((entry) => {
      if (!entry.timestamp) {
        return;
      }
      const bucketStart = config.truncate(entry.timestamp);
      const key = bucketStart.getTime();
      if (!grouped.has(key)) {
        grouped.set(key, { bucketStart, count: 1 });
      } else {
        grouped.get(key).count += 1;
      }
    });

    if (grouped.size === 0) {
      return [];
    }

    const sortedKeys = Array.from(grouped.keys()).sort((a, b) => a - b);
    const startCursor = new Date(sortedKeys[0]);
    const finalBucket = new Date(
      Math.max(
        sortedKeys[sortedKeys.length - 1],
        config.truncate(new Date()).getTime()
      )
    );

    const series = [];
    let cursor = startCursor;
    let loopProtector = 0;
    while (cursor.getTime() <= finalBucket.getTime() && loopProtector < 10_000) {
      const key = cursor.getTime();
      const bucket = grouped.get(key);
      series.push({
        bucketStart: new Date(cursor),
        count: bucket ? bucket.count : 0,
      });
      cursor = config.advance(cursor);
      loopProtector += 1;
    }

    return series.map((point, index) => {
      if (index === 0) {
        return { ...point, trend: "up" };
      }
      const prev = series[index - 1];
      const trend = point.count < prev.count ? "down" : "up";
      return { ...point, trend };
    });
  }, [entries, timelineScale]);

  const totalBreaths = entries.length;
  const breathsToday = useMemo(
    () =>
      entries.filter((entry) => entry.timestamp && isSameDay(entry.timestamp, new Date()))
        .length,
    [entries]
  );

  const lastBreath = entries.length
    ? entries[entries.length - 1]
    : null;

  const handleScaleChange = (_, nextScale) => {
    if (nextScale) {
      setTimelineScale(nextScale);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        width: "100%",
        background: pageBackground,
        py: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3, md: 6 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Stack
        spacing={4}
        sx={{
          width: "100%",
          maxWidth: "1100px",
        }}
      >
        <Paper
          sx={{
            ...panelSurface,
            p: { xs: 3, md: 4 },
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  color: alpha(
                    theme.palette.text.primary,
                    theme.palette.mode === "light" ? 0.9 : 0.95
                  ),
                }}
              >
                Breathing Practice
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "1.05rem", md: "1.1rem" },
                  color: alpha(theme.palette.text.primary, 0.78),
                  maxWidth: "720px",
                }}
              >
                Take a deliberate pause and tap the button below each time you complete a deep breath. We will chart your calm streak so you can notice trends and celebrate steady progress.
              </Typography>
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 3, md: 5 }}
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: alpha(theme.palette.text.primary, 0.82),
                  }}
                >
                  {totalBreaths}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: alpha(theme.palette.text.primary, 0.6) }}
                >
                  Total logged breaths
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: alpha(theme.palette.success.main, 0.9),
                  }}
                >
                  {breathsToday}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: alpha(theme.palette.text.primary, 0.6) }}
                >
                  Today&apos;s count
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: alpha(theme.palette.text.primary, 0.75),
                  }}
                >
                  {lastBreath?.timestamp
                    ? formatDistanceToNow(lastBreath.timestamp, {
                        addSuffix: true,
                      })
                    : "Not yet"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: alpha(theme.palette.text.primary, 0.6) }}
                >
                  Last logged breath
                </Typography>
              </Box>
            </Stack>

            <Tooltip
              placement="top"
              title="Click whenever you intentionally complete a deep breath."
            >
              <span>
                <Button
                  onClick={handleLogBreath}
                  variant="contained"
                  size="large"
                  disabled={isLogging}
                  startIcon={<AirIcon sx={{ fontSize: 32 }} />}
                  sx={{
                    alignSelf: { xs: "stretch", md: "flex-start" },
                    fontSize: { xs: "1.2rem", sm: "1.35rem" },
                    fontWeight: 700,
                    py: { xs: 2.2, sm: 2.6 },
                    px: { xs: 3, sm: 4.5 },
                    borderRadius: 3,
                    boxShadow: theme.shadows[10],
                    textTransform: "none",
                    letterSpacing: 0.4,
                  }}
                >
                  {isLogging ? "Logging..." : "Log a Deep Breath"}
                </Button>
              </span>
            </Tooltip>

            {logSuccess && (
              <Alert
                icon={<CheckCircleRoundedIcon fontSize="inherit" />}
                severity="success"
                sx={{
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.success.light, 0.2),
                  color: alpha(theme.palette.success.main, 0.9),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                  maxWidth: "420px",
                }}
              >
                Deep breath logged. Keep going—you&rsquo;re building a powerful habit.
              </Alert>
            )}

            {errorMessage && (
              <Alert
                icon={<ErrorOutlineIcon fontSize="inherit" />}
                severity="warning"
                sx={{
                  borderRadius: 3,
                  maxWidth: "540px",
                }}
              >
                {errorMessage}
              </Alert>
            )}
          </Stack>
        </Paper>

        <Paper
          sx={{
            ...panelSurface,
            p: { xs: 3, md: 4 },
          }}
        >
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: alpha(
                      theme.palette.text.primary,
                      theme.palette.mode === "light" ? 0.85 : 0.9
                    ),
                  }}
                >
                  Deep Breath Timeline
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.65),
                    maxWidth: "560px",
                  }}
                >
                  Explore your breathing rhythm across different time scales. Points that dip below the previous interval glow red; steady or rising streaks glow green.
                </Typography>
              </Box>

              <ToggleButtonGroup
                value={timelineScale}
                exclusive
                onChange={handleScaleChange}
                size="small"
                color="primary"
                sx={{
                  backgroundColor: alpha(
                    theme.palette.background.paper,
                    theme.palette.mode === "light" ? 0.6 : 0.25
                  ),
                  borderRadius: 999,
                  "& .MuiToggleButton-root": {
                    px: 2.4,
                    py: 0.8,
                    border: "none",
                    fontWeight: 600,
                    textTransform: "none",
                    letterSpacing: 0.2,
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    color: theme.palette.primary.contrastText,
                  },
                }}
              >
                {SCALE_OPTIONS.map((option) => (
                  <ToggleButton value={option.value} key={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>

            <BreathingTimeline
              data={aggregatedSeries}
              scaleConfig={scaleMap[timelineScale]}
              isLoading={loadingEntries}
              emptyStateMessage="Your breathing timeline is waiting. Log a deep breath to see your calm journey unfold."
            />
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default BreathingPage;
